/**
 * Composants propres aux écrans d'authentification.
 *
 * Les primitives génériques (`Button`, `Field`, `Hint`, `ErrorNote`) vivent
 * désormais dans `components/ui/primitives.tsx`, à l'emplacement prévu par le
 * §3 — l'étape 5 les partage avec l'écran « Aujourd'hui ». Ne restent ici que
 * les deux composants qui n'ont de sens que pour l'authentification.
 */

import type { ReactNode } from 'react';

import { ErrorNote } from '../../components/ui/primitives';
import { TOKENS } from '../../components/ui/tokens';

/** Colonne centrée occupant l'écran — support commun de connexion et d'amorçage. */
export function AuthShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        /*
          ⚠️ Les **quatre** bords, pas seulement le haut. Cet écran est le seul
          de l'application à occuper la fenêtre entière sans barre d'onglets ni
          en-tête pour absorber les encoches : en paysage, `left` et `right`
          font la différence entre un formulaire centré et un formulaire dont le
          bord passe sous l'encoche.
        */
        padding: `calc(1.5rem + env(safe-area-inset-top)) calc(1.25rem + env(safe-area-inset-right)) calc(1.5rem + env(safe-area-inset-bottom)) calc(1.25rem + env(safe-area-inset-left))`,
      }}
    >
      <section
        style={{
          width: '100%',
          maxWidth: '26rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <h1 style={{ fontSize: '1.5rem', margin: 0 }}>{title}</h1>
        {children}
      </section>
    </main>
  );
}

/**
 * Affichage de la clé de secours — **le seul endroit où elle apparaît en
 * clair**. Ni l'API ni la base ne permettent de la relire ensuite (§9) : d'où
 * la case à cocher, qui force un accusé de lecture avant de continuer.
 */
export function RecoveryKeyPanel({
  recoveryKey,
  acknowledged,
  onAcknowledge,
}: {
  recoveryKey: string;
  acknowledged: boolean;
  onAcknowledge: (value: boolean) => void;
}) {
  return (
    <>
      <ErrorNote>
        Cette clé ne sera plus jamais affichée. La recopier maintenant, hors ligne : c’est le
        seul moyen de rentrer si toutes les passkeys sont perdues.
      </ErrorNote>
      <output
        style={{
          display: 'block',
          padding: '1rem',
          borderRadius: '0.75rem',
          border: `1px solid ${TOKENS.border}`,
          background: TOKENS.surface,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '1.05rem',
          letterSpacing: '0.05em',
          lineHeight: 1.7,
          wordBreak: 'break-all',
          userSelect: 'all',
        }}
      >
        {recoveryKey}
      </output>
      <label
        style={{
          display: 'flex',
          gap: '0.625rem',
          alignItems: 'center',
          minHeight: '44px',
          fontSize: '0.95rem',
        }}
      >
        {/*
          Case à cocher ordinaire, et **pas** un interrupteur : ce n'est pas un
          réglage qu'on bascule, c'est un accusé de lecture. La distinction est
          celle que fait iOS, et VoiceOver l'annonce différemment.
        */}
        <input
          type="checkbox"
          className="ui-check"
          checked={acknowledged}
          onChange={(event) => {
            onAcknowledge(event.target.checked);
          }}
        />
        Je l’ai notée en lieu sûr.
      </label>
    </>
  );
}
